/** Une étape à l'écran : titre, blocs révélés au fil de la voix, figure à côté (16:9) ou au-dessus (9:16). */
import { FPS, readFigureSpec, readGeometrySpec, SolidSpec, type SceneState, type TimedStep, type VideoLesson } from "@jange/shared";
import { Figure } from "./Figure.js";
import { Model } from "./Model.js";
import { Geometry } from "./Geometry.js";
import { Solid } from "./Solid.js";
import { Picture } from "./Picture.js";
import { FormulaBlock, MistakeBlock, QuizBlock, TextBlock } from "./blocks.js";
import type { Format, Theme } from "../styles.js";

const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

export function StepScene({ lesson, step, state, frame, theme, format, width, height, lang, assetUrls }: { lesson: VideoLesson; step: TimedStep; state: SceneState; frame: number; theme: Theme; format: Format; width: number; height: number; lang: string; assetUrls: Record<string, string> }) {
  const lessonStep = lesson.steps[step.index];
  const blocks = lessonStep.blockIds.map((id) => lesson.blocks.find((b) => b.id === id)).filter((b): b is VideoLesson["blocks"][number] => !!b);
  const figure = blocks.find((b) => b.type === "figure");
  const model = blocks.find((b) => b.type === "scene3d");
  const geometry = blocks.find((b) => b.type === "geometry");
  const solid = blocks.find((b) => b.type === "solid3d");
  const image = blocks.find((b) => b.type === "image");
  const others = blocks.filter((b) => !["figure", "scene3d", "geometry", "solid3d", "image"].includes(b.type));
  const observation = theme.id === "observation" && !!(model || image);
  const portrait = format === "9:16";
  const pad = portrait ? 56 : 72;
  // Taille du texte selon la densité de l'étape (une question et une erreur fréquente ensemble : plus petit).
  const density = others.reduce((n, b) => n + (b.type === "quiz" ? 4 : b.type === "mistake" ? 2 : b.type === "text" ? Math.ceil(b.md.length / 120) : 1), 0);
  const size = Math.round((portrait ? (density > 6 ? 34 : 40) : figure || model || geometry || solid || image ? (observation ? 30 : 34) : density > 6 ? 32 : 40) * theme.textScale);
  const grid = !portrait && others.some((b) => b.type === "quiz" && b.choices.every((c) => c.label.length <= 28));
  const kIn = easeOut(clamp01((frame - step.from) / (0.5 * FPS)));
  const figW = portrait ? width - 2 * pad : Math.round((width - 2 * pad) * 0.52);
  const figH = portrait ? 680 : 600;
  // Observation : le modèle occupe l'écran, le texte se pose dessus (16:9) ou dessous (9:16).
  const modelW = observation ? (portrait ? width : width - 2 * pad) : figW;
  const modelH = observation ? (portrait ? 1040 : height - 230) : figH;

  const column = (
    <div style={{ display: "flex", flexDirection: "column", gap: portrait ? 22 : 26, flex: 1, minWidth: 0 }}>
      {others.map((b) => {
        const at = state.reveals.get(b.id);
        switch (b.type) {
          case "text": return <TextBlock key={b.id} block={b} reveals={state.reveals} frame={frame} theme={theme} size={size} />;
          case "formula": return <FormulaBlock key={b.id} block={b} at={at} frame={frame} theme={theme} boxed={!!lessonStep.recap} />;
          case "mistake": return <MistakeBlock key={b.id} block={b} at={at} frame={frame} theme={theme} size={size} />;
          case "quiz": return <QuizBlock key={b.id} block={b} at={at} revealAt={state.quizReveals.get(b.id)} frame={frame} theme={theme} size={size} grid={grid} />;
          default: return null;
        }
      })}
    </div>
  );

  const fig = figure && figure.type === "figure" && state.figures.get(figure.id) && (
    <Figure spec={readFigureSpec(figure.spec)} state={state.figures.get(figure.id)!} frame={frame} width={figW} height={figH} theme={theme} label={`y = ${readFigureSpec(figure.spec).curves[0]?.label ?? ""}`} />
  );
  const mdl = model && model.type === "scene3d" && state.models.get(model.id) && (
    <Model url={assetUrls[model.assetId] ?? null} hotspots={model.hotspots} state={state.models.get(model.id)!} frame={frame} width={modelW} height={modelH} lang={lang} theme={theme} credit={model.credit} />
  );
  const geoSpec = geometry && geometry.type === "geometry" ? readGeometrySpec(geometry.spec) : null;
  const geo = geometry && geoSpec && state.figures.get(geometry.id) && (
    <Geometry spec={geoSpec} state={state.figures.get(geometry.id)!} frame={frame} width={figW} height={portrait ? 760 : figH + 40} theme={theme} />
  );
  const solidSpec = solid && solid.type === "solid3d" ? SolidSpec.safeParse(solid.spec).data : undefined;
  const sol = solid && solidSpec && state.models.get(solid.id) && (
    <Solid spec={solidSpec} model={state.models.get(solid.id)!} figure={state.figures.get(solid.id)} frame={frame} width={figW} height={portrait ? 760 : figH} theme={theme} />
  );
  const pic = image && image.type === "image" && state.models.get(image.id) && (
    <Picture src={assetUrls[image.assetId] ?? null} aspect={image.aspect} markers={image.markers} state={state.models.get(image.id)!} stepFrom={step.from} frame={frame}
      width={observation ? modelW : figW} height={observation ? modelH : portrait ? 760 : figH} lang={lang} theme={theme} credit={image.credit} />
  );
  const side = fig ?? geo ?? mdl ?? sol ?? pic;

  if (observation) {
    return (
      <div style={{ position: "absolute", inset: 0, opacity: kIn, fontFamily: theme.fontFamily }}>
        <div style={{ position: "absolute", left: portrait ? 0 : pad, top: portrait ? 120 : 150, width: modelW, height: modelH }}>{mdl ?? pic}</div>
        <div style={{ position: "absolute", left: pad, top: portrait ? 60 : 98, display: "flex", alignItems: "center", gap: 18 }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: theme.accent, background: theme.accentSoft, padding: "6px 14px", borderRadius: 999 }}>Étape {step.index + 1}</span>
          <h2 style={{ margin: 0, fontSize: portrait ? 44 : 48, fontWeight: 800, color: theme.text }}>{step.title}</h2>
        </div>
        <div style={{ position: "absolute", left: pad, right: portrait ? pad : undefined, width: portrait ? undefined : 600, top: portrait ? 1190 : undefined, bottom: portrait ? undefined : 170, background: "rgba(8,20,24,0.72)", borderRadius: 20, padding: "18px 22px", backdropFilter: "blur(6px)" }}>
          {column}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "absolute", inset: 0, padding: `${pad}px ${pad}px 0`, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: portrait ? 26 : 30, opacity: kIn, transform: `translateY(${(1 - kIn) * 18}px)`, fontFamily: theme.fontFamily }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 26 }}>
        <span style={{ fontSize: Math.round((portrait ? 26 : 24) * theme.textScale), fontWeight: 800, color: theme.rough ? theme.amber : theme.accent, background: theme.rough ? "transparent" : theme.accentSoft, padding: "6px 14px", borderRadius: 999, letterSpacing: "0.04em" }}>Étape {step.index + 1}</span>
        <h2 style={{ margin: 0, fontSize: Math.round((portrait ? 50 : 52) * theme.textScale), fontWeight: 800, color: theme.text, lineHeight: 1.1 }}>{step.title}</h2>
      </div>
      {portrait ? (
        <>
          {side}
          {column}
        </>
      ) : (
        <div style={{ display: "flex", gap: 48, alignItems: "flex-start", flex: 1, minHeight: 0 }}>
          {column}
          {side && <div style={{ flexShrink: 0 }}>{side}</div>}
        </div>
      )}
      <div style={{ height: portrait ? 200 : 150, flexShrink: 0 }} />
    </div>
  );
}
